import MuiBox from '@new-components/atoms/MuiBox';
import Image from '@new-components/atoms/Image';
import MuiTypography from '@new-components/atoms/MuiTypography';
import {FeaturedCommunityType} from '@new-components/templates/HomeFeaturedCommunities';
import React from 'react';
import {MediaQuery} from '@constants/theme';
import MuiLink from '@new-components/atoms/MuiLink';
import { useAppRouter } from '@hooks/useAppRouter';
import { APP_ROUTES } from '@constants/appRoutes';

interface FeaturedCommunitySlideProps extends FeaturedCommunityType {
  hoverIndicator?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}
/**
 * A single slide component used in the FeaturedCommunityCarousel.
 *
 * @param {FeaturedCommunityType} props
 * @prop {string} title - Title of the community.
 * @prop {string} subTitle - Subtitle of the community.
 * @prop {{src: string, alt: string}} bgImg - Object containing the source and alt text of the background image.
 * @prop {{src: string, alt: string}} logoImg - Object containing the source and alt text of the logo image.
 * @prop {string} woaLink - Link to the website of the community.
 */

const FeaturedCommunitySlide: React.FC<FeaturedCommunitySlideProps> = (
  props
) => {
  const {getPathWithBase} = useAppRouter();
  return (
    <MuiLink
      onMouseEnter={props.onMouseEnter}
      onMouseOver={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      href={getPathWithBase(APP_ROUTES.COMMUNITY_DETAILS(props.woaLink))}
      target='_blank'
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        marginRight: 3,
        display: 'flex',
        flexDirection: 'column',
        rowGap: 2,
        [MediaQuery.desktop]: {
          'marginRight': 0,
          'transition': 'width .5s ease-in-out',
          'width': '25%',
          '&:hover': {
            width: '75%',
          },
        },
        ['&:hover']: {
          textDecoration: 'none !important',
        },
      }}>
      <MuiBox
        sx={{
          height: '400px',
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          transition: 'opacity .5s ease-in-out',
          opacity:
            props.hoverIndicator === undefined
              ? 1
              : props.hoverIndicator
              ? 1
              : 0.5,
        }}>
        <MuiBox
          sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
          }}>
          <Image
            // src={getAssetUrl(props.bgImg.src)}
            src={props.bgImg.src}
            alt={props.bgImg.alt}
            fill
            style={{objectFit: 'cover', zIndex: 1}}
          />
          <MuiBox
            sx={{
              background:
                ' radial-gradient(65.13% 66.62% at 50.14% 0%, #000000 0%, rgba(46, 46, 46, 0) 96.86%)',
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              opacity: 0.4,
              transform: 'rotate(180deg)',
            }}
          />
        </MuiBox>
        <MuiBox
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            py: 2,
          }}>
          <Image
            src={props.logoImg.src}
            width={160}
            height={180}
            style={{
              objectFit: 'contain',
            }}
            alt={props.logoImg.alt}
          />
        </MuiBox>
      </MuiBox>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          transition: 'opacity .5s ease-in-out',
          opacity:
            props.hoverIndicator === undefined
              ? 1
              : props.hoverIndicator
              ? 1
              : 0,
        }}>
        <MuiTypography
          sx={{
            fontSize: '17px',
            fontWeight: 500,
            lineHeight: '23.8px',
            letterSpacing: '-0.4000000059604645px',
            // Clamp content to 1 lines
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            [MediaQuery.desktop]: {
              fontWeight: 400,
              fontSize: '22px',
              lineHeight: '27.5px',
            },
          }}>
          {props.title}
        </MuiTypography>
        <MuiTypography
          typography={'paragraph'}
          sx={{
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '22.5px',
            letterSpacing: '-0.4000000059604645px',
            opacity: 0.6,
            // Clamp content to 3 lines
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}>
          {props.subTitle}
        </MuiTypography>
      </MuiBox>
    </MuiLink>
  );
};

export default FeaturedCommunitySlide;
